import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import { Link as RouterLink } from "react-router-dom";
import {
  adminListPayouts,
  approvePayout,
  rejectPayout,
  adminListCommissionRequests,
  adminPayCommissionRequest,
  adminRejectCommissionRequest,
} from "../api";

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [walletTypeFilter, setWalletTypeFilter] = useState("all");
  /* `search` is what the user typed; `appliedSearch` is what the query uses,
     so every keystroke doesn't fire a request. */
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [rejectDialogId, setRejectDialogId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(null);
  const [approveError, setApproveError] = useState(null);

  const fetchPayouts = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (statusFilter !== "all") params.status = statusFilter;
    if (walletTypeFilter !== "all") params.walletType = walletTypeFilter;
    if (appliedSearch) params.search = appliedSearch;
    adminListPayouts(params)
      .then(({ data }) => {
        setPayouts(data.payouts || []);
        setTotal(data.pagination?.total || data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, statusFilter, walletTypeFilter, appliedSearch]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  const handleApprove = async (id) => {
    setProcessing(id);
    setApproveError(null);
    try {
      const { data } = await approvePayout(id);
      if (data.slpePayoutId) {
        setApproveError({
          severity: "success",
          msg: `Approved! SLPE payout: ${data.slpePayoutId}. Polling for completion…`,
        });
      }
      fetchPayouts();
    } catch (e) {
      setApproveError({
        severity: "error",
        msg: e.response?.data?.error || "Failed to approve",
      });
    }
    setProcessing(null);
  };

  const handleReject = async () => {
    if (!rejectDialogId) return;
    setProcessing(rejectDialogId);
    try {
      await rejectPayout(rejectDialogId, rejectReason);
      setRejectDialogId(null);
      setRejectReason("");
      fetchPayouts();
    } catch (e) {
      alert(e.response?.data?.error || "Failed to reject");
    }
    setProcessing(null);
  };

  const statusColor = (s) => {
    if (s === "completed" || s === "approved") return "success";
    if (s === "rejected" || s === "failed") return "error";
    if (s === "processing") return "info";
    return "warning";
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Payout Approvals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review, validate bank accounts, and process withdrawals via SLPE
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search order ID, name, mobile…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                setAppliedSearch(search.trim());
              }
            }}
            onBlur={() => {
              if (search.trim() !== appliedSearch) {
                setPage(1);
                setAppliedSearch(search.trim());
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearch("");
                      setPage(1);
                      setAppliedSearch("");
                    }}
                  >
                    <ClearRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: 260 }}
          />
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            value={walletTypeFilter}
            onChange={(e) => {
              setWalletTypeFilter(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Wallets</MenuItem>
            <MenuItem value="commission">Commission</MenuItem>
            <MenuItem value="main">Main</MenuItem>
          </TextField>
        </Stack>
      </Stack>

      {approveError && (
        <Alert
          severity={approveError.severity}
          sx={{ mb: 2 }}
          onClose={() => setApproveError(null)}
        >
          {approveError.msg}
        </Alert>
      )}

      {/* Commission withdrawal requests — settled manually (no payout gateway). */}
      <CommissionRequestsSection />

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Bank Payouts (SLPE)
      </Typography>
      <Card sx={{ overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : payouts.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <GavelRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              No payout requests found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Wallet</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gateway</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontWeight: 700 }}
                    >
                      {p.order_no || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {p.user_name || p.account_holder_name || `User`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.role_name?.replace(/_/g, " ") || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.wallet_type === "main" ? "Main" : "Commission"}
                      color={p.wallet_type === "main" ? "primary" : "warning"}
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: 10 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    ₹{Number(p.amount || 0).toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: 12 }}
                    >
                      {p.bank_account_number
                        ? `••••${p.bank_account_number.slice(-4)}`
                        : "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.ifsc_code || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.status}
                      color={statusColor(p.status)}
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                    {p.bank_validated && (
                      <Chip
                        size="small"
                        label="Bank OK"
                        color="success"
                        variant="outlined"
                        sx={{ ml: 0.5, fontSize: 10 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {p.gateway_txn_id ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace", fontSize: 11 }}
                      >
                        {p.gateway_txn_id.slice(0, 18)}…
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                    {formatDate(p.created_at)}
                  </TableCell>
                  <TableCell align="right">
                    {p.status === "pending" && (
                      <Stack direction="row" gap={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleRoundedIcon />}
                          onClick={() => handleApprove(p.id)}
                          disabled={processing === p.id}
                          sx={{ borderRadius: 2, textTransform: "none" }}
                        >
                          {processing === p.id ? "Validating…" : "Approve"}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CancelRoundedIcon />}
                          onClick={() => setRejectDialogId(p.id)}
                          disabled={processing === p.id}
                          sx={{ borderRadius: 2, textTransform: "none" }}
                        >
                          Reject
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        )}
        {Math.ceil(total / 20) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={Math.ceil(total / 20)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Reject dialog */}
      <Dialog
        open={Boolean(rejectDialogId)}
        onClose={() => setRejectDialogId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={600}>Reject Payout</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason for rejection"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectDialogId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={processing === rejectDialogId}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Commission withdrawal requests — manual clearance queue            */
/* ------------------------------------------------------------------ */

const REQ_STATUS_COLOR = {
  pending: "warning",
  paid: "success",
  rejected: "error",
};

function CommissionRequestsSection() {
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchRequests = useCallback(() => {
    setLoading(true);
    const params = { page: 1, limit: 50 };
    if (statusFilter !== "all") params.status = statusFilter;
    adminListCommissionRequests(params)
      .then(({ data }) => {
        setRequests(data.requests || []);
        setPendingCount(data.pendingCount || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  async function handlePay(id) {
    setProcessing(id);
    setFeedback(null);
    try {
      const { data } = await adminPayCommissionRequest(id);
      setFeedback({
        severity: "success",
        msg: `Marked paid — ${INR(data.amount_paid)} deducted from the user's commission wallet.`,
      });
      fetchRequests();
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to mark paid",
      });
    }
    setProcessing(null);
  }

  async function handleReject() {
    if (!rejectId) return;
    setProcessing(rejectId);
    try {
      await adminRejectCommissionRequest(rejectId, rejectReason);
      setRejectId(null);
      setRejectReason("");
      fetchRequests();
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to reject",
      });
    }
    setProcessing(null);
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1 }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Commission Withdrawal Requests
          </Typography>
          {pendingCount > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`${pendingCount} pending`}
            />
          )}
        </Stack>
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
      </Stack>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      <Card sx={{ overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <PaidRoundedIcon
              sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              No {statusFilter === "all" ? "" : statusFilter} commission
              requests
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Balance now</TableCell>
                  <TableCell>Raised</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => {
                  const mismatch =
                    r.status === "pending" &&
                    Number(r.current_balance) !== Number(r.amount);
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography
                          component={RouterLink}
                          to={`/admin/users/${r.user_id}`}
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: "primary.main", textDecoration: "none" }}
                        >
                          {r.first_name} {r.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.role_name} · {r.mobile_number || r.email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {INR(r.amount)}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={mismatch ? "warning.main" : "text.primary"}
                        >
                          {INR(r.current_balance)}
                          {mismatch && " ⚠"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ color: "text.secondary", fontSize: 13 }}
                      >
                        {formatDate(r.created_at)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.status}
                          color={REQ_STATUS_COLOR[r.status] || "default"}
                        />
                        {r.status === "paid" && r.amount_paid != null && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            paid {INR(r.amount_paid)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {r.status === "pending" ? (
                          <Stack
                            direction="row"
                            gap={1}
                            justifyContent="flex-end"
                          >
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleRoundedIcon />}
                              disabled={processing === r.id}
                              onClick={() => handlePay(r.id)}
                            >
                              Mark Paid
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelRoundedIcon />}
                              disabled={processing === r.id}
                              onClick={() => setRejectId(r.id)}
                            >
                              Reject
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {r.admin_note || "—"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <Dialog
        open={!!rejectId}
        onClose={() => setRejectId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reject Withdrawal Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            label="Reason"
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
            placeholder="Shown to the user"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={processing === rejectId}
            onClick={handleReject}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
